/**
 * Social Media Posts Page
 * 
 * Displays social media posts from different platforms based on query parameters.
 * URL format: /smPosts?platform=fb (or ig, twitter, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SocialPost, SocialPlatform } from '../types/social';
import { SocialMediaServiceFactory, PLATFORM_CONFIGS, SOCIAL_CONFIG } from '../config/social';
import { Button } from '../components/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';

/**
 * Platform mapping for URL parameters
 */
const PLATFORM_MAP: Record<string, SocialPlatform> = {
  'fb': 'facebook',
  'facebook': 'facebook',
  'ig': 'instagram',
  'instagram': 'instagram',
  'x': 'twitter',
  'twitter': 'twitter',
  'linkedin': 'linkedin',
  'youtube': 'youtube',
  'tiktok': 'tiktok'
};

/**
 * Social media post card component
 */
interface SocialPostCardProps {
  post: SocialPost;
  className?: string;
}

const SocialPostCard: React.FC<SocialPostCardProps> = ({ post, className = '' }) => {
  const platformConfig = PLATFORM_CONFIGS[post.platform];
  const [imageError, setImageError] = useState(false);

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatMetric = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow ${className}`}>
      {/* Post Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Platform Icon */}
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
              style={{ backgroundColor: platformConfig.color }}
            >
              {platformConfig.icon}
            </div>
            
            {/* Author Info */}
            <div className="flex items-center space-x-2">
              {post.author.avatar && !imageError && (
                <img
                  src={post.author.avatar}
                  alt={post.author.displayName}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={() => setImageError(true)}
                />
              )}
              <div>
                <div className="flex items-center space-x-1">
                  <span className="font-semibold text-gray-900 text-sm">
                    {post.author.displayName}
                  </span>
                  {post.author.verified && (
                    <span className="text-blue-500">✓</span>
                  )}
                </div>
                <span className="text-gray-500 text-xs">@{post.author.username}</span>
              </div>
            </div>
          </div>
          
          {/* Platform Badge */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">{formatDate(post.timestamps.created)}</span>
            <span 
              className="px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: platformConfig.color }}
            >
              {platformConfig.displayName}
            </span>
          </div>
        </div>
      </div>

      {/* Post Content */}
      <div className="p-4">
        {/* Text Content */}
        {post.content && (
          <div className="mb-3">
            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
              {post.content}
            </p>
            
            {/* Hashtags */}
            {post.hashtags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {post.hashtags.map((hashtag, index) => (
                  <span key={index} className="text-blue-600 text-sm">
                    #{hashtag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Media Content */}
        {post.media.length > 0 && (
          <div className="mb-3">
            {post.media.length === 1 ? (
              // Single media item
              <div className="rounded-lg overflow-hidden">
                {post.media[0].type === 'image' ? (
                  <img
                    src={post.media[0].url}
                    alt={post.media[0].altText || 'Post image'}
                    className="w-full h-auto max-h-96 object-cover"
                    loading="lazy"
                  />
                ) : post.media[0].type === 'video' ? (
                  <video
                    src={post.media[0].url}
                    poster={post.media[0].thumbnail}
                    controls
                    className="w-full h-auto max-h-96"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : null}
              </div>
            ) : (
              // Multiple media items (carousel)
              <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden">
                {post.media.slice(0, 4).map((item, index) => (
                  <div key={item.id} className="relative">
                    {item.type === 'image' ? (
                      <img
                        src={item.url}
                        alt={item.altText || `Post image ${index + 1}`}
                        className="w-full h-32 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-xs">Video</span>
                      </div>
                    )}
                    {post.media.length > 4 && index === 3 && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className="text-white font-semibold">
                          +{post.media.length - 4} more
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Post Metrics */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center space-x-1">
              <span>❤️</span>
              <span>{formatMetric(post.metrics.likes)}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span>💬</span>
              <span>{formatMetric(post.metrics.comments)}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span>🔄</span>
              <span>{formatMetric(post.metrics.shares)}</span>
            </span>
            {post.metrics.views && post.metrics.views > 0 && (
              <span className="flex items-center space-x-1">
                <span>👁️</span>
                <span>{formatMetric(post.metrics.views)}</span>
              </span>
            )}
          </div>
          
          {/* External Link */}
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View Original →
          </a>
        </div>
      </div>
    </div>
  );
};

/**
 * Platform selector component
 */
interface PlatformSelectorProps {
  currentPlatform: SocialPlatform;
  onPlatformChange: (platform: SocialPlatform) => void;
  availablePlatforms: SocialPlatform[];
}

const PlatformSelector: React.FC<PlatformSelectorProps> = ({
  currentPlatform,
  onPlatformChange,
  availablePlatforms
}) => {
  return (
    <div className="flex flex-wrap gap-2">
      {availablePlatforms.map((platform) => {
        const config = PLATFORM_CONFIGS[platform];
        const isActive = currentPlatform === platform;
        
        return (
          <button
            key={platform}
            onClick={() => onPlatformChange(platform)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
              isActive
                ? 'bg-white border-gray-300 shadow-md'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <span className="text-lg">{config.icon}</span>
            <span className="font-medium text-gray-900">{config.displayName}</span>
            {isActive && (
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Main Social Media Posts Page Component
 */
export const SocialMediaPosts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Get platform from URL parameters
  const platformParam = searchParams.get('platform') || 'fb';
  const currentPlatform: SocialPlatform = PLATFORM_MAP[platformParam] || 'facebook';
  
  // Available platforms (could be filtered based on configuration)
  const availablePlatforms: SocialPlatform[] = ['facebook', 'instagram', 'twitter'];

  /**
   * Fetch posts from selected platform
   */
  const fetchPosts = useCallback(async (platform: SocialPlatform, reset = false) => {
    setLoading(true);
    setError(null);

    try {
      const service = SocialMediaServiceFactory.getService(platform, SOCIAL_CONFIG.useMockData);
      
      const response = await service.getUserPosts({
        platform,
        limit: SOCIAL_CONFIG.defaultLimit,
        nextToken: reset ? undefined : nextToken,
        username: platform === 'twitter' ? 'devlife' : undefined, // Mock username for Twitter
        includeReplies: false,
        includeRetweets: false
      });

      if (reset) {
        setPosts(response.data);
      } else {
        setPosts(prev => [...prev, ...response.data]);
      }

      setNextToken(response.meta.nextToken || null);
      setHasMore(!!response.meta.nextToken);

    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [nextToken]);

  /**
   * Handle platform change
   */
  const handlePlatformChange = useCallback((platform: SocialPlatform) => {
    const platformKey = Object.keys(PLATFORM_MAP).find(key => PLATFORM_MAP[key] === platform) || platform;
    setSearchParams({ platform: platformKey });
    setPosts([]);
    setNextToken(null);
    setHasMore(true);
    setError(null);
  }, [setSearchParams]);

  /**
   * Load more posts
   */
  const loadMore = useCallback(() => {
    if (!loading && hasMore && nextToken) {
      fetchPosts(currentPlatform, false);
    }
  }, [loading, hasMore, nextToken, currentPlatform, fetchPosts]);

  /**
   * Refresh posts
   */
  const refresh = useCallback(() => {
    setPosts([]);
    setNextToken(null);
    setHasMore(true);
    fetchPosts(currentPlatform, true);
  }, [currentPlatform, fetchPosts]);

  // Load posts when platform changes
  useEffect(() => {
    fetchPosts(currentPlatform, true);
  }, [currentPlatform]);

  const platformConfig = PLATFORM_CONFIGS[currentPlatform];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Social Media Posts
              </h1>
              <p className="text-gray-600">
                Browse posts from different social media platforms
              </p>
            </div>
            
            <Button onClick={refresh} variant="outline" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" /> : '🔄'} Refresh
            </Button>
          </div>

          {/* Platform Selector */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Select Platform</h2>
            <PlatformSelector
              currentPlatform={currentPlatform}
              onPlatformChange={handlePlatformChange}
              availablePlatforms={availablePlatforms}
            />
          </div>
        </div>
      </div>

      {/* Current Platform Info */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div 
          className="bg-white rounded-lg p-4 border-l-4 mb-6"
          style={{ borderLeftColor: platformConfig.color }}
        >
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{platformConfig.icon}</span>
            <div>
              <h3 className="font-semibold text-gray-900">
                {platformConfig.displayName} Posts
              </h3>
              <p className="text-sm text-gray-600">
                {SOCIAL_CONFIG.useMockData ? 'Using demo data' : 'Live data from API'}
                {posts.length > 0 && ` • ${posts.length} posts loaded`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <span className="text-red-600 text-xl mr-3">⚠️</span>
              <div>
                <h3 className="text-red-800 font-semibold">Error Loading Posts</h3>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {posts.length === 0 && !loading && !error && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📱</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts found</h3>
            <p className="text-gray-600 mb-4">
              No posts available for {platformConfig.displayName} at the moment.
            </p>
            <Button onClick={refresh} variant="primary">
              Try Again
            </Button>
          </div>
        )}

        {posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <SocialPostCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-8">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-3 text-gray-600">
                Loading {platformConfig.displayName} posts...
              </p>
            </div>
          </div>
        )}

        {/* Load More Button */}
        {posts.length > 0 && hasMore && !loading && (
          <div className="text-center mt-8">
            <Button onClick={loadMore} variant="outline" size="lg">
              Load More Posts
            </Button>
          </div>
        )}

        {/* End of Posts Message */}
        {posts.length > 0 && !hasMore && !loading && (
          <div className="text-center mt-8 py-4">
            <p className="text-gray-500">
              You've reached the end of available posts
            </p>
          </div>
        )}
      </div>

      {/* API Configuration Notice */}
      {SOCIAL_CONFIG.useMockData && (
        <div className="bg-blue-50 border-t border-blue-200">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-start">
              <span className="text-blue-600 text-xl mr-3">ℹ️</span>
              <div>
                <h4 className="text-blue-900 font-semibold">Demo Mode</h4>
                <p className="text-blue-800 text-sm mt-1">
                  Currently showing demo data. To use real social media data, configure your API credentials in the environment variables:
                </p>
                <ul className="text-blue-700 text-xs mt-2 space-y-1">
                  <li>• Facebook: VITE_FACEBOOK_ACCESS_TOKEN</li>
                  <li>• Instagram: VITE_INSTAGRAM_ACCESS_TOKEN, VITE_INSTAGRAM_BUSINESS_ACCOUNT_ID</li>
                  <li>• Twitter: VITE_TWITTER_BEARER_TOKEN, VITE_TWITTER_USERNAME</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaPosts;